import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import {AgentAction, AgentChange, AgentChangeStatus, AgentChat, AgentEvent, AgentField, AgentPageContext, AgentQuestion} from '../domain/ai-agent';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, filter, Subscription} from 'rxjs';
import {NavigationStart, Router} from '@angular/router';
import {ApiService} from '../../../../infrastructure/service/api.service';
import {AIAgentHandler} from './ai-agent-handler';

@Injectable({
  providedIn: 'root'
})
export class AiAgentService extends ApiService {

  private serviceUrl = 'ai-agent';

  public event: BehaviorSubject<AgentEvent | undefined> = new BehaviorSubject<AgentEvent | undefined>(undefined);

  protected router: Router;

  protected locale: string;

  public aiChanges: Map<string, AgentChange> = new Map<string, AgentChange>();
  private pageContext?: AgentPageContext;

  constructor(http: HttpClient, @Inject(LOCALE_ID) locale: string, router: Router) {
    super(http);
    this.router = router;
    this.locale = locale;
    this.bindPageContextLifecycle();
  }

  public subscribe(observer: any): Subscription {
    return this.event.subscribe(observer);
  }

  public dispachEvent(event: AgentEvent): void {
    this.event.next(event);
  }

  public defaultHandleAction(context: AIAgentHandler, action: AgentAction): void {
    if (action) {
      action.fields?.forEach((field: AgentField) => this.defaultHandleField(context, field));
    }
  }

  public defaultHandleField(context: AIAgentHandler, field: AgentField): void {
    if (!field?.id) {
      return;
    }

    try {
      if (context.handleAiFieldFill) {
        context.handleAiFieldFill(field);
        context.getAiChanges().delete(field.id);
        this.aiChanges = context.getAiChanges();
        return;
      }

      const oldValue: any = context.getData()[field.id];
      context.getAiChanges().set(field.id, {oldValue, newValue: field.value, status: AgentChangeStatus.CREATED});
      this.aiChanges = context.getAiChanges();
    } catch (e) {
      console.warn(e);
    }
  }

  defaultUndo(context: any): void {
    try {
      context.aiChanges = new Map<string, AgentChange>();
    } catch (e) {
      console.warn(e);
    }
  }

  defaultSave(context: any): void {
    try {
      context.handleAiSave();
      context.getAiChanges().forEach((agentChange: AgentChange) => agentChange.status = AgentChangeStatus.SAVED);
    } catch (e) {
      console.warn(e);
    }
  }

  ask(question: string, chatId?: string, clientRequestId?: string, attachments: File[] = []): Promise<AgentQuestion> {
    const request = this.buildQuestion(question, chatId, clientRequestId);

    if (!attachments.length) {
      return this.post(`${this.serviceUrl}`, request).toPromise();
    }

    const formData = new FormData();
    formData.append('request', new Blob([JSON.stringify(request)], {type: 'application/json'}));
    attachments.forEach((attachment) => formData.append('attachments', attachment, attachment.name));

    return this.post(`${this.serviceUrl}`, formData).toPromise();
    // return this.mockedResponse(question);
  }

  confirmPlan(question: string, chatId?: string, clientRequestId?: string): Promise<AgentQuestion> {
    return this.post(`${this.serviceUrl}`, this.buildQuestion(question, chatId, clientRequestId, {
      inlineConfirmation: 'true'
    })).toPromise();
  }

  public setPageContext(pageContext?: AgentPageContext | null): void {
    this.pageContext = pageContext ? {...pageContext} : undefined;
  }

  public clearPageContext(): void {
    this.pageContext = undefined;
  }

  private buildQuestion(question: string,
                        chatId?: string,
                        clientRequestId?: string,
                        context?: Record<string, string>): AgentQuestion {
    const now = new Date();
    const pageContext = this.buildRequestPageContext(now);
    const body: AgentQuestion = {
      chat: {
        id: chatId
      },
      context: {
        userLocale: this.locale,
        timezone: this.resolveUserTimezone(),
        nowUtc: now.toISOString(),
        utcOffsetMinutes: String(-now.getTimezoneOffset()),
        clientRequestId,
        pageContext,
        ...(context || {})
      },
      question
    };

    return body;
  }

  findChatById(id: string): Promise<AgentChat> {
    return this.get(`${this.serviceUrl}/${id}`).toPromise();
  }

  findChats(): Promise<AgentChat[]> {
    return this.get(`${this.serviceUrl}`).toPromise();
  }

  findQuestionsByChat(id: string): Promise<AgentQuestion[]> {
    return this.get(`${this.serviceUrl}/${id}/questions`).toPromise();
  }

  updateChatTitle(id: string, title: string): Promise<AgentChat> {
    return this.patch(`${this.serviceUrl}/${id}`, this.toGenericDTO({name: title})).toPromise();
  }

  public compareToActiveRoute(pattern: string): boolean {
    const url = this.router.url;

    const regexPattern = pattern
      .replace(/:[^\s/]+/g, '[^/]+') // converte :id em [^/]+
      .replace(/\*/g, '.*');         // converte * em .*

    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(url);
  }

  private resolveUserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  }

  private buildRequestPageContext(now: Date): AgentPageContext {
    if (this.pageContext) {
      return {...this.pageContext};
    }

    return {
      route: this.router?.url ?? '',
      capturedAt: now.toISOString()
    };
  }

  private bindPageContextLifecycle(): void {
    const routerEvents = (this.router as any)?.events;
    if (!routerEvents?.pipe) {
      return;
    }

    routerEvents
      .pipe(filter((event: unknown) => event instanceof NavigationStart))
      .subscribe(() => this.clearPageContext());
  }

  undoField(field: AgentField): void {
    // TODO: Criar rotina para limpar um campo apenas, tem que trabalhar com os eventos
  }

  mockedResponse(question: string): Promise<AgentQuestion> {
    const response: AgentQuestion = JSON.parse('{\n' +
      '    "id": "e88f0fbc-3e7b-46ca-9f9e-6489e967cc5a",\n' +
      '    "tenantId": "e45b87a2-50b4-4395-8259-6f8110c70e7e",\n' +
      '    "user": null,\n' +
      '    "context": null,\n' +
      '    "question": "Como crio uma reunião para as 10h da próxima quarta-feira com o clente LegalManager?",\n' +
      '    "answer": "{\\n  \\"responseProps\\": {\\n    \\"timezone\\": \\"UTC+3\\",\\n    \\"locale\\": \\"pt-BR\\"\\n  },\\n  \\"message\\": \\"Para criar uma reunião para as 10h da próxima quarta-feira com o cliente LegalManager, siga os passos abaixo:\\",\\n  \\"actions\\": [\\n    {\\n      \\"operation\\": \\"create\\",\\n      \\"url\\": \\"/tasks/add\\",\\n      \\"htmlFile\\": \\"app/modules/task/task-edit/task-edit.component.html\\",\\n      \\"message\\": \\"Abra a tela de criação de tarefas\\",\\n      \\"fields\\": [\\n        {\\n          \\"id\\": \\"name\\",\\n          \\"name\\": \\"Nome\\",\\n          \\"value\\": \\"Reunião com o cliente LegalManager\\"\\n        },\\n        {\\n          \\"id\\": \\"description\\",\\n          \\"name\\": \\"Descrição\\",\\n          \\"value\\": \\"Reunião para discutir o projeto LegalManager\\"\\n        },\\n        {\\n          \\"id\\": \\"dueDate\\",\\n          \\"name\\": \\"Data de vencimento\\",\\n          \\"value\\": \\"2025-09-18\\"\\n        },\\n        {\\n          \\"id\\": \\"startTime\\",\\n          \\"name\\": \\"Hora de início\\",\\n          \\"value\\": \\"10:00\\"\\n        },\\n        {\\n          \\"id\\": \\"endTime\\",\\n          \\"name\\": \\"Hora de fim\\",\\n          \\"value\\": \\"12:00\\"\\n        }\\n      ]\\n    }\\n  ],\\n  \\"api\\": {\\n    \\"method\\": \\"POST\\",\\n    \\"url\\": \\"/ai-agent/tasks\\",\\n    \\"request\\": {\\n      \\"name\\": \\"Reunião com o cliente LegalManager\\",\\n      \\"description\\": \\"Reunião para discutir o projeto LegalManager\\",\\n      \\"dueDate\\": \\"2025-09-18\\",\\n      \\"startTime\\": \\"10:00\\",\\n      \\"endTime\\": \\"12:00\\"\\n    }\\n  }\\n}",\n' +
      '    "active": true\n' +
      '}');
    return Promise.resolve(response);
  }
}
